import { Component, computed, inject, Input, signal } from "@angular/core";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
    VisualizationEntity,
    SerieEntity,
    PointEntity,
    CompanyEntity
} from "@arch/domain/entity";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { ResponseComparationType } from "@arch/infra/repository/map/point-map.repository";
import { FormatNumbers } from "@arch/_shared/utils/format-number.utils";
import { ComparationPercentageValueComponent } from "../../comparation-percentage-value/comparation-percentage-value.component";
import { StringUtils } from "@arch/_shared/utils/String.utils";
import { DeviceService } from "@app/shared/services/device.service";
import { ColorsService } from "@arch/domain/application/services/colors.service";
import { ThemeService } from "@arch/domain/application/services/theme.service";

@Component({
    selector: "app-indicator-values",
    imports: [
        FlexLayoutModule,
        MatTooltipModule,
        ComparationPercentageValueComponent
    ],
    templateUrl: "./indicator-values.component.html",
    styleUrl: "./indicator-values.component.scss"
})
export class IndicatorValuesComponent {
    private themeService = inject(ThemeService);

    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: true }) set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    private optionsSignal = signal<VisualizationOptionsType>(null);

    public currentValueColorByTheme: string = "";

    @Input({ required: false }) isSpecialChart: boolean = false;
    @Input({ required: true }) series: SerieEntity[];
    @Input({ required: true }) points: PointEntity[];
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) isDrilldown: boolean = false;
    @Input({ required: false }) suffix?: string = "";

    private readonly deviceService = inject(DeviceService);
    public readonly isMobile = this.deviceService.getIsMobile();

    public readonly seriesComparationsView = computed(
        (): SeriesComparationViewType[] => {
            const theme = this.themeService.getThemeName();
            const colorThemeSpecial = ColorsService.getColor(
                this.visualization.props.component?.configs?.series?.[0]
                    .color || "basic.dark"
            );
            this.currentValueColorByTheme =
                theme !== "default"
                    ? ColorsService.getColor(
                          this.visualization.props?.component?.configs?.series?.[0].color.split(
                              "."
                          )[0] + ".light" || ""
                      )
                    : "";

            if (!this.optionsSignal()) return [];
            const comparations = this.visualization.comparationsValues;

            const seriesComparation = comparations
                .map((comparation) => {
                    const currentValue = FormatNumbers.byUnit(
                        comparation.serie.props.unit,
                        comparation.current,
                        2,
                        !this.isSpecialChart
                    );
                    const percentage = FormatNumbers.byUnit(
                        "percentage",
                        Math.abs(comparation.percentage * 100),
                        2
                    );
                    const polarityColor = this.isSpecialChart
                        ? colorThemeSpecial
                        : comparation.polarityColor;
                    const icon = this.getPolarityIcon(comparation);
                    const tooltip = comparation.explanationVariation;
                    return {
                        serieName: `${comparation.serie.props.name} ${this.suffix || ""}`,
                        serieNameNd: StringUtils.normalizeND(
                            comparation.serie.props.name,
                            /[^A-z]/g
                        ),
                        currentValue,
                        percentage,
                        polarityColor,
                        icon,
                        tooltip,
                        i: comparation?.serie?.props?.i
                    };
                })
                .sort((a, b) => a.i - b.i);

            return seriesComparation;
        }
    );

    private getPolarityIcon(comparation: ResponseComparationType): string {
        if (comparation?.variationCondition === 0) return "fa-solid fa-minus";
        if (comparation?.variationCondition === 1)
            return "fa-solid fa-caret-up";
        return "fa-solid fa-caret-down";
    }
}

type SeriesComparationViewType = {
    serieName: string;
    serieNameNd?: string;
    currentValue: string;
    percentage: string;
    polarityColor: string;
    icon: string;
    tooltip: string;
};
